import clsx from 'clsx';
import type { JSX, ReactNode } from 'react';

type HeadingLayout = 'stacked' | 'split';
type HeadingAlign = 'start' | 'center';
type HeadingLevel = 'h1' | 'h2';

interface PublicPageHeadingProps {
  eyebrow?: string;
  title: string;
  description?: ReactNode;
  actions?: ReactNode;
  layout?: HeadingLayout;
  align?: HeadingAlign;
  className?: string;
  contentClassName?: string;
  as?: HeadingLevel;
}

const ALIGN_CLASS: Record<HeadingAlign, string> = {
  start: 'items-start text-left',
  center: 'items-center text-center',
};

export function PublicPageHeading({
  eyebrow,
  title,
  description,
  actions,
  layout = 'stacked',
  align = 'start',
  className,
  contentClassName,
  as = 'h2',
}: PublicPageHeadingProps): JSX.Element {
  const HeadingTag = as;
  const containerClassName = clsx(
    'flex flex-col gap-4',
    layout === 'split'
      ? 'md:flex-row md:items-end md:justify-between md:gap-8'
      : ALIGN_CLASS[align],
    className,
  );

  const contentAlignment =
    layout === 'split'
      ? 'text-left'
      : align === 'center'
        ? 'items-center text-center'
        : 'items-start text-left';

  const hasEyebrow = typeof eyebrow === 'string' && eyebrow.length > 0;
  const hasDescription = description !== null && description !== undefined;
  const hasActions = actions !== null && actions !== undefined;

  return (
    <header className={containerClassName}>
      <div className={clsx('flex flex-col gap-2', contentAlignment, contentClassName)}>
        {hasEyebrow ? (
          <p className="text-sm font-semibold uppercase tracking-wide text-brand-accent">
            {eyebrow}
          </p>
        ) : null}
        <HeadingTag className="text-3xl font-semibold text-brand-primary md:text-4xl">
          {title}
        </HeadingTag>
        {hasDescription ? (
          <div className="max-w-3xl text-base text-text-primary/80">{description}</div>
        ) : null}
      </div>
      {hasActions ? <div className="flex items-center gap-3">{actions}</div> : null}
    </header>
  );
}
